import React, { useMemo } from 'react'
import copy from 'copy-to-clipboard'
import {
  ElementType,
  isLayerElement,
  isTopLevelFrameElement,
  SketchElement,
  SketchLayerCSSProperties,
  SymbolMaster,
} from '../../../inspector'
import { ElementIcon } from '../../../shares/FrameView/components/FrameDetailInspectorContextMenu/ElementIcon'
import { useGenerateLayerCSS } from '../../../shares/FrameView/components/FrameDetailInspector/useGenerateLayerCSS'
import { useExportModal } from '../../../shares/FrameView/components/FrameDetailInspector/sections/Exports/ExportModal'
import { Dropdown, Tooltip } from '@sketch/components'
import { FlatTreeNode } from './types'
import { VirtualItem } from '@tanstack/react-virtual'
import { useToast } from '@sketch/toasts'
import { useNavigateToFrame } from '../../hooks/useNavigateToFrame'
import { useInspectorContext } from '../../../shares/FrameView/hooks/inspector'
import { useSymbolMasterDocument } from '../../../shares/FrameView/hooks/inspector/useSymbolMasterDocument'
import { routes } from '@sketch/modules-common'

import { ReactComponent as CopyCSSIcon } from '@sketch/icons/img-markdown-code-inline-17'
import { ReactComponent as CopyLinkIcon } from '@sketch/icons/img-markdown-link-share-17'
import { ReactComponent as ExportIcon } from '@sketch/icons/img-arrow-down-circle-17'
import { ReactComponent as GoToLayerIcon } from '@sketch/icons/arrow-right-circle-semifilled-17'
import { ReactComponent as OpenFrameIcon } from '@sketch/icons/frame-17'
import { ReactComponent as SymbolIcon } from '@sketch/icons/symbol-17'

import * as S from './LayersTreeItem.styles'
import {
  useCreateLayerLink,
  useShouldEnableCopyLinkToLayer,
} from './useCreateLayerLink'
import { ErrorHandler } from '@sketch/tracing'
import { useVersioning } from '../../../versioning'

type LayersTreeItemProps = {
  layer: FlatTreeNode
  isSelected: boolean
  isHovered: boolean
  onSelect: (sketchElement: SketchElement) => void
  onHover: (sketchElement: SketchElement | null) => void
  onToggleCollapse: (identifier: bigint) => void
  goToLayer: (sketchElement: SketchElement) => void
  virtualItem: VirtualItem
  onContextMenu: (event: React.MouseEvent, sketchElement: SketchElement) => void
}

export function LayersTreeItem({
  layer,
  isSelected,
  isHovered,
  onSelect,
  onHover,
  onToggleCollapse,
  goToLayer,
  virtualItem,
  onContextMenu,
}: LayersTreeItemProps) {
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false)
  // Different from isHovered prop which represents the hover state of the inspector
  // ( isHovered = hover of canvas or tree item | isHoveringTreeItem = hover of the tree item itself)
  const [isHoveringTreeItem, setIsHoveringTreeItem] = React.useState(false)
  const layerTreeItemRef = React.useRef<HTMLLIElement>(null)
  const lastMouseTargetRef = React.useRef<EventTarget | null>(null)

  const { sketchElement, level, hasChildren, isCollapsed, isLayerMasked } =
    layer

  const isSymbol =
    sketchElement.type === 'symbolMaster' ||
    sketchElement.type === 'symbolInstance'

  const handleMouseEnter = () => {
    setIsHoveringTreeItem(true)
    onHover(sketchElement)
  }

  const isLoadingFragment =
    isTopLevelFrameElement(sketchElement) && sketchElement.hasFragmentPending

  const handleMouseLeave = () => {
    setIsHoveringTreeItem(false)
    onHover(null)
  }

  const handleToggleCollapse = (event: React.MouseEvent) => {
    event.stopPropagation()
    onToggleCollapse(sketchElement.identifier)
  }

  const handleRightClick = (event: React.MouseEvent) => {
    // Select the layer before opening the context menu
    onSelect(sketchElement)
    onContextMenu(event, sketchElement)
  }

  // Track mouse movements globally only when dropdown is open
  React.useEffect(() => {
    if (!isDropdownOpen) return

    const handleGlobalMouseMove = (event: MouseEvent) => {
      lastMouseTargetRef.current = event.target
    }

    document.addEventListener('mousemove', handleGlobalMouseMove)
    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove)
    }
  }, [isDropdownOpen])

  // Close ellipsis dropdown when context menus open
  React.useEffect(() => {
    const handleCloseDropdown = () => {
      if (isDropdownOpen) {
        setIsDropdownOpen(false)
      }
    }

    window.addEventListener('canvas-context-menu-opened', handleCloseDropdown)
    window.addEventListener(
      'layer-tree-context-menu-opened',
      handleCloseDropdown
    )

    return () => {
      window.removeEventListener(
        'canvas-context-menu-opened',
        handleCloseDropdown
      )
      window.removeEventListener(
        'layer-tree-context-menu-opened',
        handleCloseDropdown
      )
    }
  }, [isDropdownOpen])

  const handleDropdownToggle = (visible: boolean) => {
    setIsDropdownOpen(visible)

    // When the dropdown closes, we can't be sure what triggered the close,
    // it could be a click outside the dropdown, a click on an action within it,
    // or a click on the tree item that owns it.
    //
    // Because the dropdown menu is rendered inside the tree item, hovering over the menu
    // also counts as hovering over the row. However, once the dropdown closes,
    // we need to re-evaluate whether the mouse is still hovering the row or not.
    if (!visible && layerTreeItemRef.current && lastMouseTargetRef.current) {
      const isMouseOverRow =
        lastMouseTargetRef.current instanceof Node &&
        layerTreeItemRef.current.contains(lastMouseTargetRef.current)

      setIsHoveringTreeItem(isMouseOverRow)
    }
  }

  const verticalLines = useMemo(() => {
    return Array.from({ length: level }).map((_, index) => (
      <S.VerticalLine key={index} level={index} />
    ))
  }, [level])

  return (
    <S.VirtualLayerItem
      ref={layerTreeItemRef}
      role="treeitem"
      aria-selected={isSelected}
      // Keep the hover highlight when the dropdown is open
      $isHovered={isHovered || isDropdownOpen}
      $level={level}
      $isSymbol={isSymbol}
      onClick={() => onSelect(sketchElement)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onContextMenu={handleRightClick}
      style={{
        height: `${virtualItem.size}px`,
        transform: `translateY(${virtualItem.start}px)`,
      }}
    >
      {verticalLines}
      <ItemIcon
        // don't change the icon while dropdown is open to avoid flickering the icon
        // while the focus should be on the dropdown.
        isHoveringTreeItem={isHoveringTreeItem || isDropdownOpen}
        hasChildren={hasChildren}
        isCollapsed={isCollapsed}
        handleToggleCollapse={handleToggleCollapse}
        sketchElement={sketchElement}
        isLayerMasked={isLayerMasked}
        isLoading={isLoadingFragment}
        isSelected={isSelected}
      />
      <S.LayerName $isSymbol={isSymbol}>{sketchElement.name}</S.LayerName>
      {!isLoadingFragment && (
        // Prevent actions from selecting the
        <div onClick={e => e.stopPropagation()}>
          <Dropdown
            toggle={
              <S.EllipsisButton
                $isVisible={isHoveringTreeItem || isDropdownOpen}
                aria-label="Layer options"
              >
                <S.EllipsisIcon />
              </S.EllipsisButton>
            }
            placement="top-start"
            usePortal={true}
            onToggle={handleDropdownToggle}
            visible={isDropdownOpen}
          >
            <LayerItemActions
              sketchElement={sketchElement}
              onGoToLayer={goToLayer}
              onSelect={onSelect}
            />
          </Dropdown>
        </div>
      )}
    </S.VirtualLayerItem>
  )
}

type ItemIconProps = {
  isHoveringTreeItem: boolean
  hasChildren: boolean
  isCollapsed: boolean
  handleToggleCollapse: (event: React.MouseEvent) => void
  sketchElement: SketchElement
  isLayerMasked: boolean
  isLoading?: boolean
  isSelected?: boolean
}

function ItemIcon({
  isHoveringTreeItem,
  hasChildren,
  isCollapsed,
  handleToggleCollapse,
  sketchElement,
  isLayerMasked,
  isLoading,
  isSelected,
}: ItemIconProps) {
  if (isLoading) {
    return (
      <S.SpinnerContainer data-testid="layer-loading-spinner">
        <S.LoadingSpinner />
      </S.SpinnerContainer>
    )
  }
  let content = (
    <ElementIcon
      elementType={sketchElement.type}
      isLayerMasked={isLayerMasked}
      isLayerExportable={sketchElement.isExportable}
      isSelected={isSelected}
    />
  )

  if (isHoveringTreeItem && hasChildren) {
    content = (
      <S.ToggleButton
        onClick={handleToggleCollapse}
        aria-label="Toggle layer children visibility"
      >
        <S.ToggleIcon $isCollapsed={isCollapsed} />
      </S.ToggleButton>
    )
  }

  return <S.ItemIconContainer>{content}</S.ItemIconContainer>
}

export type LayerItemActionsProps = {
  sketchElement: SketchElement
  onGoToLayer: (sketchElement: SketchElement) => void
  onSelect: (sketchElement: SketchElement) => void
}

type ForeignSymbolOpenActionProps = {
  symbolMaster: SymbolMaster
}

/**
 * Foreign symbols need to be resolved first before we can open them (we don't know the share id).
 * It can be that we end-up with a symbol the user doesn't have access to.
 */
function ForeignSymbolOpenAction({
  symbolMaster,
}: ForeignSymbolOpenActionProps) {
  const { frame, shareIdentifier } = useSymbolMasterDocument(symbolMaster)

  const isSymbolAccessible = Boolean(frame && shareIdentifier)

  const handleClick = () => {
    if (!isSymbolAccessible) return

    const url =
      routes.FRAME.create({
        shareID: shareIdentifier!,
        frameUUID: frame!.uuid,
      }) + '#Inspect'

    window.open(url, '_blank')
  }

  if (isSymbolAccessible) {
    return (
      <S.StyledDropdownItem onClick={handleClick}>
        <S.MenuIcon as={SymbolIcon} />
        Open Symbol Source
      </S.StyledDropdownItem>
    )
  }

  return (
    <div onClick={e => e.stopPropagation()}>
      <Tooltip
        placement="top"
        content="This Symbol belongs to a Library that is unavailable"
      >
        <S.StyledDropdownItem disabled={true}>
          <S.MenuIcon as={SymbolIcon} />
          Open Symbol Source
        </S.StyledDropdownItem>
      </Tooltip>
    </div>
  )
}

export function LayerItemActions({
  sketchElement,
  onGoToLayer,
  onSelect,
}: LayerItemActionsProps) {
  const { showToast } = useToast()
  const { openExportModal } = useExportModal()
  const navigateToFrame = useNavigateToFrame()
  const { sketchSceneRootElement } = useInspectorContext()
  const { getPathname, share } = useVersioning()

  const layerCSS = useGenerateLayerCSS(
    sketchElement as SketchLayerCSSProperties
  )
  const createLayerLink = useCreateLayerLink()

  const handleCopyCSSClick = () => {
    if (!layerCSS.css.length) return
    copy(layerCSS.toString())
    showToast('CSS copied')
  }

  const handleCopyLinkClick = () => {
    const url = createLayerLink(sketchElement.layerPathHash)
    if (url) {
      copy(url)
      showToast('Link copied')
    }
  }

  const handleExportAsClick = () => {
    openExportModal(sketchElement.identifier, sketchElement.name)
  }

  const { nearestOpenableFrame } = sketchElement

  let symbolMaster: SymbolMaster | undefined = undefined
  if (sketchElement.type === ElementType.SymbolInstance) {
    symbolMaster = sketchElement.symbolMaster
  } else if (isLayerElement(sketchElement)) {
    symbolMaster = sketchElement.symbolInstanceAncestorElement?.symbolMaster
  }

  // Check if we need symbol resolution (for library symbols)
  const needsSymbolResolution = Boolean(symbolMaster?.isForeign)

  const handleOpenFrameClick = () => {
    if (!nearestOpenableFrame) return

    if (
      nearestOpenableFrame.type === ElementType.Artboard ||
      nearestOpenableFrame.type === ElementType.SymbolMaster
    ) {
      // For artboards and symbol masters, use the regular navigation
      navigateToFrame(nearestOpenableFrame.targetUUID, 'layerListAction')
      return
    }

    if (needsSymbolResolution) {
      ErrorHandler.shouldNeverHappen(
        'Foreign symbol resolution should be handled by ForeignSymbolOpenAction component'
      )
      return
    }
    // From here we are expecting the nearest openable frame to be part of the document.

    // Symbol sources are opened in a new tab,
    const url = getPathname({
      routeKey: 'FRAME',
      routeParams: {
        shareID: share.identifier,
        frameUUID: nearestOpenableFrame.targetUUID,
      },
    })
    window.open(url, '_blank')
  }

  const shouldEnableCopyLink = useShouldEnableCopyLinkToLayer()

  const handleGoToLayerClick = () => {
    onSelect(sketchElement)
    onGoToLayer(sketchElement)
  }

  // When in the frame view, sketchSceneRootElement will be equal to the currently open frame.
  const isOpenableFrameCurrentlyOpen =
    nearestOpenableFrame &&
    sketchSceneRootElement &&
    nearestOpenableFrame.targetUUID === sketchSceneRootElement.elementUUID

  function getNearestOpenableFrameLabel() {
    if (!nearestOpenableFrame) return undefined

    if (nearestOpenableFrame.type === ElementType.Artboard) {
      return nearestOpenableFrame.isSelf ? 'Open Frame' : 'Open Top-Level Frame'
    } else if (nearestOpenableFrame.type === ElementType.SymbolMaster) {
      return nearestOpenableFrame.isSelf ? 'Open Symbol' : 'Open Parent Symbol'
    } else if (nearestOpenableFrame.type === ElementType.SymbolInstance) {
      return 'Open Symbol Source'
    }
  }

  return (
    <>
      <Dropdown.Header> Layer </Dropdown.Header>
      {nearestOpenableFrame && !isOpenableFrameCurrentlyOpen && (
        <>
          {needsSymbolResolution && (
            <ForeignSymbolOpenAction symbolMaster={symbolMaster!} />
          )}
          {!needsSymbolResolution && (
            <S.StyledDropdownItem onClick={handleOpenFrameClick}>
              <S.MenuIcon
                as={
                  nearestOpenableFrame.type === ElementType.Artboard
                    ? OpenFrameIcon
                    : SymbolIcon
                }
              />
              {getNearestOpenableFrameLabel()}
            </S.StyledDropdownItem>
          )}
        </>
      )}
      <S.StyledDropdownItem onClick={handleGoToLayerClick}>
        <S.MenuIcon as={GoToLayerIcon} />
        Go to Layer
      </S.StyledDropdownItem>
      {shouldEnableCopyLink && (
        <S.StyledDropdownItem onClick={handleCopyLinkClick}>
          <S.MenuIcon as={CopyLinkIcon} />
          Copy Link
        </S.StyledDropdownItem>
      )}
      {layerCSS.css.length > 0 && (
        <S.StyledDropdownItem onClick={handleCopyCSSClick}>
          <S.MenuIcon as={CopyCSSIcon} />
          Copy CSS
        </S.StyledDropdownItem>
      )}
      <S.StyledDropdownItem onClick={handleExportAsClick}>
        <S.MenuIcon as={ExportIcon} />
        Export as…
      </S.StyledDropdownItem>
    </>
  )
}
